import * as React from "react";
import { ScrollText } from "lucide-react";
import { invalidateSkillsCatalog, type SkillsController } from "../skills";
import type { SkillSummary } from "../skills/types";
import type { ComposerAttachmentMenuAction } from "./ComposerAttachmentPicker";
import { SkillsBrowserPanel, type SkillsBrowserCloseReason } from "./SkillsBrowserPanel";

function closeOptions(reason: SkillsBrowserCloseReason | undefined) {
  if (reason === "back") {
    return { restoreFocus: false, reopenMenu: true };
  }
  if (reason === "explore" || reason === "dismiss") {
    return { restoreFocus: false };
  }
  return { restoreFocus: true };
}

/**
 * Shared plus-menu Skills row for Excel and PowerPoint. Hosts keep their own
 * extra actions and Explore handler. The panel chrome is FE-3144; selection,
 * catalog, and Explore are the shared skills controller.
 */
export function createSkillsPickerMenuAction(options: {
  controller: SkillsController;
  onExploreSkills: () => void;
  onSelectSkill?: (skill: SkillSummary) => void;
}): ComposerAttachmentMenuAction {
  const { controller, onExploreSkills, onSelectSkill } = options;

  return {
    id: "skills",
    label: "Skills",
    icon: <ScrollText aria-hidden />,
    trailingChevron: true,
    secondaryPanel: {
      open: controller.pickerOpen,
      onOpenChange: controller.setPickerOpen,
      render: ({ anchorRef, onOpenChange }) => (
        <SkillsBrowserPanel
          open={controller.pickerOpen}
          status={controller.status}
          skills={controller.skills}
          selectedSkills={controller.selectedSkills}
          search={controller.pickerSearch}
          sourceFilter={controller.pickerSourceFilter}
          catalogError={controller.catalogError}
          anchorRef={anchorRef}
          onSearchChange={controller.setPickerSearch}
          onSourceFilterChange={controller.setPickerSourceFilter}
          onSelect={onSelectSkill ?? controller.addSelectedSkill}
          onExploreSkills={onExploreSkills}
          onRetry={invalidateSkillsCatalog}
          onOpenChange={(open, reason) => onOpenChange(open, closeOptions(reason))}
        />
      ),
    },
  };
}
